import React from 'react';
import type { Column } from 'react-table';

import { capitalize } from 'utils/string';
import { getFormattedDate } from 'utils/date';

import PopOver from 'components/common/popover';
import PillStatus from 'components/common/pill/PillStatus';
import TableHeader from 'components/common/table/TableHeader';
import AvatarLinkComponent from 'components/common/avatar/AvatarLinkComponent';

import { NOTE_STATUS_TYPES, DATE_FORMAT_FOR_DISPLAY } from 'constants/appConstants';
import { NOTE_SUMMARY_LABEL_LENGTH } from 'constants/string';

import type { NoteSummaryColumnCellProps, NoteSummaryItem } from './interfaces';

interface GetNoteSummaryColumnsProps {
  columnToSortBy: string;
  isAscendingOrdered: boolean;
  onHeaderClick: (name: string) => void;
}

export function getNoteSummaryColumns({
  columnToSortBy,
  isAscendingOrdered,
  onHeaderClick,
}: GetNoteSummaryColumnsProps): Array<Column<NoteSummaryItem>> {
  return [
    {
      Header: <TableHeader label="SN." identifier="sn" isAscendingOrdered={isAscendingOrdered} />,
      accessor: 'sn',
      maxWidth: 80,
      className: 'd-flex align-items-center',
      Cell: ({ original: note }: NoteSummaryColumnCellProps) => <div className="text-center">{note.sn}</div>,
    },
    {
      Header: (
        <TableHeader
          label="Title"
          identifier="title"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => {
            onHeaderClick('title');
          }}
        />
      ),
      accessor: 'title',
      maxWidth: 250,
      className: 'd-flex align-items-center',
      Cell: ({ original: note }: NoteSummaryColumnCellProps) => (
        <PopOver
          visible={note.label.length > NOTE_SUMMARY_LABEL_LENGTH}
          interactive
          html={
            <div className="tooltip-info word-break">
              <p className="tooltip-info__title">{note.label}</p>
            </div>
          }
          customClass="ellipsis"
        >
          <span className="ellipsis">{note.label}</span>
        </PopOver>
      ),
    },
    {
      Header: (
        <TableHeader
          label="Status"
          identifier="status"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => onHeaderClick('status')}
        />
      ),
      accessor: 'status',
      maxWidth: 150,
      className: 'd-flex align-items-center',
      Cell: ({ original: note }: NoteSummaryColumnCellProps) => (
        <PillStatus isActive={note.status === NOTE_STATUS_TYPES.ACTIVE} label={capitalize(note.status)} />
      ),
    },
    {
      Header: (
        <TableHeader
          label="Action Date"
          identifier="actionDate"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => onHeaderClick('actionDate')}
        />
      ),
      accessor: 'actionDate',
      maxWidth: 150,
      className: 'd-flex align-items-center',
      Cell: ({ original: note }: NoteSummaryColumnCellProps) =>
        note.actionDate ? getFormattedDate(note.actionDate, DATE_FORMAT_FOR_DISPLAY) : '-',
    },
    {
      Header: (
        <TableHeader
          label="Created Date"
          identifier="createdDate"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => onHeaderClick('createdDate')}
        />
      ),
      accessor: 'createdDate',
      maxWidth: 150,
      className: 'd-flex align-items-center',
      Cell: ({ original: note }: NoteSummaryColumnCellProps) => (
        <>{getFormattedDate(note.createdAt, DATE_FORMAT_FOR_DISPLAY)}</>
      ),
    },
    {
      Header: (
        <TableHeader
          label="Notes of"
          identifier="noteOf"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => onHeaderClick('noteOf')}
        />
      ),
      accessor: 'noteOf',
      maxWidth: 300,
      className: 'd-flex align-items-center',
      Cell: ({ original: note }: NoteSummaryColumnCellProps) => (
        <AvatarLinkComponent
          empId={note.user.empId}
          id={note.user.id}
          name={note.user.fullname || note.user.fullName || ''}
        />
      ),
    },
    {
      Header: (
        <TableHeader
          label="Creator"
          identifier="creator"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => onHeaderClick('creator')}
        />
      ),
      accessor: 'creator',
      maxWidth: 300,
      className: 'd-flex align-items-center',
      Cell: ({ original: note }: NoteSummaryColumnCellProps) => (
        <AvatarLinkComponent empId={note.noteCreator.empId} id={note.noteCreator.id} name={note.noteCreator.fullName} />
      ),
    },
  ];
}
